import { Modal } from "@mui/material";
import React, { useRef, useState } from "react";
import { bulkZoneValidationSchema } from "../../../../constants/validaitonSchema";
import { createDate, DATE } from "../../../../constants/variables/dates";
import { actions } from "../../../../redux/store/store";
import RouteDateRange from "../RouteDateRange";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import { useOutsideAlerter } from "../../../../constants/outsideAlert";
import Timepicker from "../../Timepicker";
import { API_SUCCESS } from "../../../../constants/variables/successMessage";
import { useUpdateBulkZonesMutation } from "../../../../api/orders";
import { sendSuccessMessage } from "../../../../constants/extras/sendSuccessMsg";
import { checkForError } from "../../../../constants/extras/errorHandlers";
import { ErrorMessage } from "../../Form";
import MetroSelect from "../../metroSelect";
import HubSelect from "../../hubSelect";
import MultiSelect from "../../../../constants/multiSelectDropdown";
import { bulkUpdateShiftTypeReactSelectStyle } from "../../../../constants/multiSelectDropdown/multiSelectStyle";
import { shiftReportStatus } from "../../../../constants/menuLists";
import { useGetMetrosQuery } from "../../../../api/metro";
import { useGetZonesQuery } from "../../../../api/zones";

// Statuses that can be bulk zone-edited (must mirror the server allow-list).
const BULK_ZONE_ALLOWED_STATUSES = [
  "created",
  "reopened",
  "reserved",
  "assigned",
  "delivery_started",
  "complete",
];
const shiftStatusOptions = shiftReportStatus.filter((s) =>
  BULK_ZONE_ALLOWED_STATUSES.includes(s.value),
);

// Render the select menus in a portal so they aren't clipped by the modal's
// scroll container, and float above the modal.
const dropdownStyles = {
  ...bulkUpdateShiftTypeReactSelectStyle,
  menuPortal: (base) => ({ ...base, zIndex: 100000 }),
};

function BulkZoneModal(props) {
  const { fromDate, toDate } = props?.id || {};
  const initArr = [
    {
      startDate: new Date(fromDate),
      endDate: new Date(toDate),
      key: "selection",
    },
  ];
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [state, setState] = useState(initArr);
  const [hub, setHub] = useState({ name: "", id: "" });

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef, setShowDateRangePicker);
  const { UPDATED } = API_SUCCESS.SHIFT;

  const [update, { isLoading: isUpdating }] = useUpdateBulkZonesMutation();

  const form = useForm({
    defaultValues: {
      metro: "",
      hub: { name: "", id: "" },
      zones: [],
      newZones: [],
      shiftStatus: [],
      startTime: "",
    },
    resolver: yupResolver(bulkZoneValidationSchema),
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const metro = watch("metro");
  const { data: metros } = useGetMetrosQuery(null);
  const selectedMetroCode = metros?.find((m) => m.name === metro)?.code;

  const { data: zonesData, isLoading: isZonesLoading } = useGetZonesQuery(
    selectedMetroCode,
    { skip: !selectedMetroCode },
  );

  const rawZones = selectedMetroCode
    ? Array.isArray(zonesData)
      ? zonesData?.[0]?.zones
      : zonesData?.zones
    : [];
  const zonesOptions = (rawZones || []).map((zone) => ({
    label: zone,
    value: zone,
  }));

  const onCancel = () => {
    actions.modal.closeBulkZoneModal();
  };

  const handleCustomRange = () => setShowDateRangePicker(!showDateRangePicker);
  const handleCloseDtPickr = () => setShowDateRangePicker(false);

  const handleDataWithDtFormatting = (state) => {
    return {
      startDate: createDate(state[0]?.startDate, DATE.Y_M_D),
      endDate: createDate(state[0]?.endDate, DATE.Y_M_D),
    };
  };

  const handleHubFilter = (hubObj, onChange) => {
    const { name, _id: id } = hubObj || {};
    const obj = { name: name ? name : "", id: id ? id : "" };
    setHub(obj);
    onChange(obj);
  };

  const onSubmit = async (data) => {
    const dateRangeData = handleDataWithDtFormatting(state);

    const formData = {
      fromDate: dateRangeData?.startDate,
      endDate: dateRangeData?.endDate,
      metro: data?.metro,
      hub: data?.hub?.id,
      zones: (data?.zones || []).map((item) => item.value),
      newZones: (data?.newZones || []).map((item) => item.value),
      shiftStatus: (data?.shiftStatus || []).map((item) => item.value),
      update_startTime: data?.startTime,
    };

    const result = await update(formData);

    if (!result?.error && result?.data?.status === 200) {
      sendSuccessMessage(UPDATED);
      actions.refetch.setIsDataRefetch(true);
      actions.modal.closeBulkZoneModal();
    } else checkForError(result);
  };

  const DateRangeSelectProps = {
    form: form,
    showDateRangePicker,
    fromDate: state[0]?.startDate,
    toDate: state[0]?.endDate,
    state,
    className: "w-100",
    title: "Select Route Date*",
    formTitle: { fromDate: "fromDate", toDate: "toDate" },
    handleCloseDtPickr,
    handleCustomRange,
    handleChangeDtRange: (item) => setState(item),
  };

  const Label = ({ component, title, className, htmlFor, style }) => {
    const CustomizeComponent = component || "label";
    return (
      <CustomizeComponent
        style={style || null}
        className={`form__label ${className || ""}`}
        htmlFor={htmlFor || ""}
      >
        {title}
      </CustomizeComponent>
    );
  };

  return (
    <Modal
      open
      onClose={onCancel}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className={`modal after-loading`} style={{ width: "30rem" }}>
        <div className="modal__header">
          <h2 className="modal__title">Bulk Zone Update</h2>
        </div>
        <div className="modal__body">
          <div>
            <RouteDateRange {...DateRangeSelectProps} />
          </div>

          <div
            className="d-flex flex-wrap justify-content-between"
            style={{ paddingBottom: "0.625rem" }}
          >
            <div style={{ width: "47%" }}>
              <Label title={"Select Metro*"} />
              <Controller
                control={control}
                name="metro"
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error },
                }) => (
                  <React.Fragment>
                    <MetroSelect
                      value={value || ""}
                      onChange={(e, data) => {
                        onChange(data);
                        // reset zone selections when metro changes
                        setValue("zones", []);
                        setValue("newZones", []);
                      }}
                      ref={ref}
                      isError={!!error}
                      width={"100%"}
                    />
                    <ErrorMessage error={error} />
                  </React.Fragment>
                )}
              />
            </div>

            <div style={{ width: "47%" }}>
              <Label title={"Select Hub*"} />
              <Controller
                control={control}
                name="hub"
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error },
                }) => (
                  <React.Fragment>
                    <HubSelect
                      value={value || { name: "", id: "" }}
                      onChange={(event, newValue) =>
                        handleHubFilter(newValue, onChange)
                      }
                      sx={{ width: 215 }}
                      ref={ref}
                    />
                    <ErrorMessage error={error} />
                  </React.Fragment>
                )}
              />
            </div>
          </div>

          <div style={{ paddingBottom: "0.625rem" }}>
            <Label title={"Match Zones*"} />
            <Controller
              control={control}
              name="zones"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <React.Fragment>
                  <MultiSelect
                    placeholder="Select zones to match"
                    itemData={zonesOptions}
                    styles={dropdownStyles}
                    menuPortalTarget={document.body}
                    value={value}
                    isLoading={isZonesLoading}
                    onChange={(selected) => onChange(selected || [])}
                  />
                  <ErrorMessage error={error} />
                </React.Fragment>
              )}
            />
          </div>

          <div style={{ paddingBottom: "0.625rem" }}>
            <Label title={"Shift Status"} />
            <Controller
              control={control}
              name="shiftStatus"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <React.Fragment>
                  <MultiSelect
                    placeholder="Defaults to Created/Reopened/Reserved/Assigned"
                    itemData={shiftStatusOptions.map((item) => ({
                      value: item.value,
                      label: item.title,
                    }))}
                    styles={dropdownStyles}
                    menuPortalTarget={document.body}
                    value={value}
                    onChange={(selected) => onChange(selected || [])}
                  />
                  <ErrorMessage error={error} />
                </React.Fragment>
              )}
            />
          </div>

          <div style={{ paddingBottom: "0.625rem" }}>
            <Label title={"New Zones*"} />
            <Controller
              control={control}
              name="newZones"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <React.Fragment>
                  <MultiSelect
                    placeholder="Select new zones"
                    itemData={zonesOptions}
                    styles={dropdownStyles}
                    menuPortalTarget={document.body}
                    value={value}
                    isLoading={isZonesLoading}
                    onChange={(selected) => onChange(selected || [])}
                  />
                  <ErrorMessage error={error} />
                </React.Fragment>
              )}
            />
          </div>

          <div style={{ width: "47%" }}>
            <Label title="Start Time:" />
            <Controller
              control={control}
              name="startTime"
              render={({ field: { value }, fieldState: { error } }) => (
                <Timepicker
                  value={value}
                  updateTravelTime={(val) => setValue("startTime", val)}
                  error={error}
                />
              )}
            />
          </div>
        </div>
        <div className="modal__footer">
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="btn btn_accent"
            onClick={handleSubmit(onSubmit)}
            disabled={isUpdating}
          >
            Update
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default BulkZoneModal;
